import React, { useCallback, useEffect, useState } from 'react';
import { Camera, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Skeleton } from '../ui/skeleton';
import { adminGet, adminPut, reportError } from './adminApi';

const JOBS = [['green', 'Green (jardinage)'], ['clean', 'Clean (ménage)'], ['diy', 'DIY (bricolage)']];

/** Règle « photos avant / après au pointage » : moments, métiers concernés, obligatoire ou non, messages. */
export function AdminPhotoRulesCard({ adminKey }) {
  const [rules, setRules] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await adminGet('/api/admin/easer/photo-rules', adminKey);
      setRules(r.data.rules);
      setDirty(false);
    } catch (err) {
      reportError('règle photos', err, (m) => toast.error(m || 'Chargement impossible'));
    }
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => { setRules((r) => ({ ...r, ...patch })); setDirty(true); };
  const toggleJob = (job, on) => update({ jobs: on ? [...new Set([...(rules.jobs || []), job])] : (rules.jobs || []).filter((j) => j !== job) });

  const save = async () => {
    setSaving(true);
    try {
      const r = await adminPut('/api/admin/easer/photo-rules', adminKey, {}, rules);
      setRules(r.data.rules);
      setDirty(false);
      toast.success('Règle photos enregistrée');
    } catch (err) {
      reportError('règle photos', err, (m) => toast.error(m || 'Enregistrement impossible'));
    } finally {
      setSaving(false);
    }
  };

  if (!rules) return <Skeleton className="h-40 rounded-xl" />;

  return (
    <Card className="p-5 space-y-4" data-testid="admin-photo-rules">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2"><Camera className="h-4 w-4" /> Photos avant / après au pointage</h3>
          <p className="text-xs text-muted-foreground">Juste après le pointage Début (et/ou Fin), l'application propose aux Easers des métiers cochés de prendre des photos, déposées sur la fiche d'intervention Dolibarr.</p>
        </div>
        <Button size="sm" onClick={save} disabled={saving || !dirty} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="photo-rules-save">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Enregistrer</Button>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <label className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm"><span>Photos <strong>avant</strong> au pointage Début</span><Switch checked={!!rules.before_enabled} onCheckedChange={(v) => update({ before_enabled: v })} data-testid="photo-rules-before" /></label>
        <label className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm"><span>Photos <strong>après</strong> au pointage Fin</span><Switch checked={!!rules.after_enabled} onCheckedChange={(v) => update({ after_enabled: v })} data-testid="photo-rules-after" /></label>
        <label className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm"><span>Obligatoire <span className="text-xs text-muted-foreground block">au moins une photo, pas de bouton « Pas de photo »</span></span><Switch checked={!!rules.mandatory} onCheckedChange={(v) => update({ mandatory: v })} data-testid="photo-rules-mandatory" /></label>
      </div>
      <div>
        <Label className="text-xs">Métiers concernés (tag Dolibarr de l'Easer)</Label>
        <div className="flex flex-wrap gap-4 mt-1.5">
          {JOBS.map(([id, label]) => (
            <label key={id} className="flex items-center gap-2 text-sm"><Checkbox checked={(rules.jobs || []).includes(id)} onCheckedChange={(c) => toggleJob(id, !!c)} data-testid={`photo-rules-job-${id}`} /> {label}</label>
          ))}
        </div>
        {(rules.jobs || []).length === 0 && <p className="text-xs text-muted-foreground mt-1">Aucun métier coché : la règle s'applique à tous les Easers.</p>}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div><Label htmlFor="photo-rules-before-msg" className="text-xs">Message « avant »</Label><Textarea id="photo-rules-before-msg" rows={2} value={rules.before_message || ''} onChange={(e) => update({ before_message: e.target.value })} maxLength={300} data-testid="photo-rules-before-message" /></div>
        <div><Label htmlFor="photo-rules-after-msg" className="text-xs">Message « après »</Label><Textarea id="photo-rules-after-msg" rows={2} value={rules.after_message || ''} onChange={(e) => update({ after_message: e.target.value })} maxLength={300} data-testid="photo-rules-after-message" /></div>
      </div>
    </Card>
  );
}
